import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Reveal, GoldRule } from "@/components/Shared";
import {
  HairHero, HairBreadcrumb, HairBand, HairEyebrow, HairHeading, Bullets, Paras, HairRiskPanel, HairFaq, HairCard, DoctorBlock, PathwayStrip, PracticeStandards, HairCTABand,
} from "@/components/HairShared";
import { StickyCTA } from "@/components/Landing";
import { OFFERS } from "@/data/landing";
import { HAIR_BREADCRUMBS, HAIR_HERO, TRANSPLANT_HUB } from "@/data/hair";

const TransplantHub = () => {
  const t = TRANSPLANT_HUB;
  const crumbs = HAIR_BREADCRUMBS.transplant();

  return (
    <>
      <SEO
        title="Hair Transplant Perth — Cottesloe | Lumière Hair Clinic"
        description="Hair transplantation planned and performed by Dr Kristy Truong at Lumière Hair Clinic, Cottesloe. Assessment first. Honest about who it suits."
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "MedicalProcedure",
              name: "Hair transplantation",
              description: t.standfirst,
              url: "https://www.lumiereaesthetics.com.au/hair/transplantation",
              provider: { "@type": "MedicalClinic", name: "Lumière Aesthetics", address: "Shop 3/40 Marine Parade, Cottesloe WA 6011", telephone: "+61 8 6388 4474" },
            },
            { "@type": "FAQPage", mainEntity: t.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
            { "@type": "BreadcrumbList", itemListElement: crumbs.map((c, i) => ({ "@type": "ListItem", position: i + 1, name: c.name, item: `https://www.lumiereaesthetics.com.au${c.to}` })) },
          ],
        }}
      />
      <HairHero eyebrow={t.eyebrow} title={t.title} standfirst={t.standfirst} offer={OFFERS.advanced} image={HAIR_HERO.room} testId="transplant-hero" />
      <HairBreadcrumb items={crumbs} />

      <HairBand narrow testId="transplant-in-brief" className="!py-0">
        <Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center border-b border-hairline/40 pb-12">
            {t.inBrief.map(([label, value]) => (
              <div key={label}>
                <HairEyebrow>{label}</HairEyebrow>
                <p className="font-serif font-normal text-ink text-xl mt-2">{value}</p>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal className="pt-12">
          <HairEyebrow className="mb-4">What it is</HairEyebrow>
          <Paras items={t.whatItIs} />
        </Reveal>
      </HairBand>

      <HairBand dark narrow testId="transplant-who-it-suits">
        <Reveal>
          <HairEyebrow className="mb-4">Who it suits — and who it doesn't</HairEyebrow>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 mt-6">
            <div>
              <h3 className="text-warm-white mb-4">It suits</h3>
              <Bullets items={t.suits} dark />
            </div>
            <div>
              <h3 className="text-warm-white mb-4">It doesn't — or not yet</h3>
              <Bullets items={t.notSuits} dark />
            </div>
          </div>
          <p className="font-serif italic text-hairline text-xl leading-relaxed mt-10 border-l-2 border-gold pl-8 py-2" data-testid="transplant-decline-note">{t.declineNote}</p>
        </Reveal>
      </HairBand>

      <PathwayStrip />

      <HairBand dark testId="transplant-your-doctor">
        <Reveal><DoctorBlock dark /></Reveal>
      </HairBand>

      <HairBand narrow testId="transplant-cost">
        <Reveal>
          <HairEyebrow className="mb-4">Cost</HairEyebrow>
          <HairHeading>Quoted in writing, after assessment.</HairHeading>
          <p className="text-ink/85 leading-[1.8] text-[17px] mt-6">{t.costNote}</p>
          <Link to="/hair/transplantation/cost" data-testid="transplant-cost-link" className="inline-block mt-5 text-[12px] tracking-[0.16em] uppercase font-medium text-deep-sea hover:text-gold transition-colors duration-300">Cost, inclusions and payment →</Link>
        </Reveal>
      </HairBand>

      <HairBand dark narrow testId="transplant-risks">
        <Reveal><HairRiskPanel risks={t.risks} dark /></Reveal>
      </HairBand>

      <PracticeStandards />

      <HairBand dark testId="transplant-subpages">
        <Reveal>
          <HairEyebrow className="mb-3">In detail</HairEyebrow>
          <HairHeading dark>Read before you decide.</HairHeading>
          <GoldRule className="mt-6" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {t.subPages.map((s) => (
              <HairCard key={s.to} to={s.to} title={s.title} text={s.text} dark testId={`transplant-subcard-${s.to.split("/").pop()}`} />
            ))}
          </div>
        </Reveal>
      </HairBand>

      <HairBand narrow testId="transplant-faq">
        <Reveal>
          <HairEyebrow className="mb-4">Questions</HairEyebrow>
          <HairFaq faqs={t.faqs} testId="transplant-faq" />
          <Link to="/hair/transplantation/faq" data-testid="transplant-all-faq-link" className="inline-block mt-6 text-[12px] tracking-[0.16em] uppercase font-medium text-deep-sea hover:text-gold transition-colors duration-300">All questions →</Link>
        </Reveal>
      </HairBand>

      <StickyCTA practitioner="Dr Kristy Truong" hair />
      <HairCTABand />
    </>
  );
};

export default TransplantHub;
